import { FormGroup } from '@angular/forms';
import { Form, FormField } from '../interfaces/form.interface';
import type { ReactiveChangeOrigin } from './change-origin/reactive-change-origin';
import { ReactiveDependencyGraph } from './dependencies/reactive-dependency-graph';
import { DropdownSelectedValueService } from './form-operations/dropdown-selected-value.service';
import { DropdownFilterExecutor } from './form-operations/dropdown-filter.executor';
import { DropdownTransformExecutor } from './form-operations/dropdown-transform.executor';
import { FieldLogicExecutor } from './form-operations/field-logic.executor';
import { FieldUpdateExecutor } from './form-operations/field-update.executor';
import { FormFieldFinder } from './form-operations/form-field.finder';
import { FormOperationState } from './form-operations/form-operation-state';

/**
 * Facade for dynamic reactive-form operations.
 * The public API is intentionally kept stable for existing form-builder usages.
 */
export class FormOperations<T> {
    private readonly state = new FormOperationState();
    private readonly fieldFinder: FormFieldFinder<T>;
    private readonly selectedValueService = new DropdownSelectedValueService<T>();
    private readonly filterExecutor: DropdownFilterExecutor<T>;
    private readonly logicExecutor: FieldLogicExecutor<T>;

    constructor(form: Form<T>, model: FormGroup) {
        this.fieldFinder = new FormFieldFinder<T>(form);

        const dependencyGraph = new ReactiveDependencyGraph<T>(form);

        this.filterExecutor = new DropdownFilterExecutor<T>(model, this.state, this.selectedValueService, dependencyGraph);

        const transformExecutor = new DropdownTransformExecutor<T>(model);
        const updateExecutor = new FieldUpdateExecutor<T>(model, this.state, this.selectedValueService);

        this.logicExecutor = new FieldLogicExecutor<T>(
            this.fieldFinder,
            model,
            transformExecutor,
            this.filterExecutor,
            updateExecutor,
            this.state,
            dependencyGraph,
        );
    }

    /**
     * Returns the field attached to a control name.
     */
    public getFieldByControlName(controlName: string | undefined): FormField<T> | undefined {
        return this.fieldFinder.getFieldByControlName(controlName);
    }

    /**
     * Attaches the passed value to the legacy dropdown selection compatibility store.
     */
    public applySelected(field: FormField<T> | undefined, value: T | null): void {
        this.selectedValueService.applySelected(field, value);
    }

    /**
     * Executes dynamic field logic in a deterministic async-safe order.
     */
    public executeFieldsLogic(changedField: string | null, origin: ReactiveChangeOrigin = 'programmatic'): Promise<void> {
        return this.logicExecutor.execute(changedField, origin);
    }

    /**
     * Filter dropdown values by the id of another dropdown.
     */
    public getFilteredValues(previousValue: string, field: FormField<T>): (T | null)[] | NonNullable<T> | string {
        return this.filterExecutor.getFilteredValues(previousValue, field);
    }
}
